import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Alert, Button, Card, Col, Empty, Form, Input, Row, Select, Space, Statistic, Switch, Table, Tabs, Tag, Tooltip } from 'antd';
import {
  Shield,
  Users,
  Database,
  Activity,
  Mail,
  Gamepad2,
  Building2,
  AlertTriangle,
  CheckCircle2,
  FileJson,
  Save,
  Plus,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AdminUsers from './AdminUsers';
import AdminTelemetry from './AdminTelemetry';
import AdminPlayerData from './AdminPlayerData';
import InternalLayout from './InternalLayout';
import './AdminCenter.css';

const slugify = (value) => value
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)+/g, '');

const parseManifestText = (text = '') => {
  if (!text.trim()) return null;
  return JSON.parse(text);
};

const describeBridge = (module) => (
  module.supports_auth_bridge || module.supports_telemetry
    ? 'Bridge enabled'
    : 'Legacy module'
);

const runtimeOptions = [
  { value: 'unity-webgl', label: 'Unity WebGL' },
  { value: 'html-iframe', label: 'HTML iframe' },
];

const AdminCenter = ({ defaultSection = 'overview' }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'org_admin' || user?.role === 'platform_admin';
  const isPlatformAdmin = user?.role === 'platform_admin';

  const [activeSection, setActiveSection] = useState(defaultSection);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [saving, setSaving] = useState(false);

  // Email templates (platform admin only)
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState('');
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  const [emailLogTotal, setEmailLogTotal] = useState(0);
  const [emailLogPage, setEmailLogPage] = useState(1);

  // Modules + subjects (platform admin only)
  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [newSubject, setNewSubject] = useState({ name: '' });
  const [newModule, setNewModule] = useState({
    module_id: '',
    title: '',
    subject: 'physics',
    subject_id: null,
    build_path: '',
    cover_image_url: '',
    runtime_type: 'unity-webgl',
    manifest_text: ''
  });
  const [manifestStatus, setManifestStatus] = useState({ type: '', text: '', metadata: null });

  // Organizations (platform admin only)
  const [organizations, setOrganizations] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: '', domain: '' });

  const authHeaders = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
  }), []);

  const visibleSubjects = useMemo(() => subjects.filter((subject) => subject.is_active), [subjects]);

  useEffect(() => {
    setActiveSection(defaultSection);
  }, [defaultSection]);

  const fetchTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const response = await axios.get('/api/admin/email-templates', authHeaders);
      const nextTemplates = response.data || [];
      setTemplates(nextTemplates);
      setSelectedTemplateKey((current) => (
        current || nextTemplates[0]?.key || ''
      ));
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load email templates.' });
    } finally {
      setTemplatesLoading(false);
    }
  }, [authHeaders]);

  const fetchEmailLogs = useCallback(async (page = 1) => {
    setEmailLogsLoading(true);
    try {
      const response = await axios.get('/api/admin/email-logs', {
        ...authHeaders,
        params: {
          page,
          page_size: 25
        }
      });
      setEmailLogs(response.data?.items || []);
      setEmailLogTotal(response.data?.total || 0);
      setEmailLogPage(response.data?.page || page);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load email logs.' });
    } finally {
      setEmailLogsLoading(false);
    }
  }, [authHeaders]);

  const fetchModules = useCallback(async () => {
    setModulesLoading(true);
    try {
      const response = await axios.get('/api/admin/modules', authHeaders);
      setModules(response.data || []);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load modules.' });
    } finally {
      setModulesLoading(false);
    }
  }, [authHeaders]);

  const fetchSubjects = useCallback(async () => {
    setSubjectsLoading(true);
    try {
      const response = await axios.get('/api/admin/subjects', authHeaders);
      setSubjects(response.data || []);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load subjects.' });
    } finally {
      setSubjectsLoading(false);
    }
  }, [authHeaders]);

  const fetchOrganizations = useCallback(async () => {
    setOrgLoading(true);
    try {
      const response = await axios.get('/api/admin/organizations', authHeaders);
      setOrganizations(response.data || []);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load organizations.' });
    } finally {
      setOrgLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (!isAdmin) return;
    if (isPlatformAdmin) {
      fetchOrganizations();
    }
  }, [fetchOrganizations, isAdmin, isPlatformAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    // lazy-load big sections
    if (isPlatformAdmin) {
      if (activeSection === 'templates') {
        fetchTemplates();
        fetchEmailLogs(1);
      }
      if (activeSection === 'modules') {
        fetchSubjects();
        fetchModules();
      }
    }
  }, [activeSection, fetchEmailLogs, fetchModules, fetchSubjects, fetchTemplates, isAdmin, isPlatformAdmin]);

  const handleTemplateChange = (id, field, value) => {
    setTemplates((prev) => prev.map((tmpl) => (
      tmpl.id === id ? { ...tmpl, [field]: value } : tmpl
    )));
  };

  const saveTemplate = async (template) => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await axios.put(`/api/admin/email-templates/${template.key}`, {
        name: template.name,
        subject: template.subject,
        body: template.body,
        is_active: template.is_active
      }, authHeaders);
      setTemplates((prev) => prev.map((item) => (
        item.key === template.key ? response.data : item
      )));
      setMessage({ type: 'success', text: `Template ${template.key} saved.` });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to save template.' });
    } finally {
      setSaving(false);
    }
  };

  const removeSubject = async (subject) => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await axios.put(`/api/admin/subjects/${subject.id}`, { is_active: false }, authHeaders);
      setSubjects((prev) => prev.map((item) => (item.id === subject.id ? { ...item, is_active: false } : item)));
      setMessage({ type: 'success', text: 'Subject removed.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to remove subject.' });
    } finally {
      setSaving(false);
    }
  };

  const createSubject = async () => {
    const trimmed = newSubject.name.trim();
    if (!trimmed) return;

    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const payload = {
        key: slugify(trimmed),
        name: trimmed,
        sort_order: subjects.length * 10,
        is_active: true
      };
      const response = await axios.post('/api/admin/subjects', payload, authHeaders);
      setSubjects((prev) => [response.data, ...prev]);
      setNewSubject({ name: '' });
      setMessage({ type: 'success', text: 'Subject created.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to create subject.' });
    } finally {
      setSaving(false);
    }
  };

  const handleModuleChange = (id, field, value) => {
    setModules((prev) => prev.map((module) => (
      module.id === id ? { ...module, [field]: value } : module
    )));
  };

  const saveModule = async (module) => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await axios.put(`/api/admin/modules/${module.module_id}`, {
        title: module.title,
        description: module.description,
        subject: module.subject,
        subject_id: module.subject_id,
        build_path: module.build_path,
        cover_image_url: module.cover_image_url,
        runtime_type: module.runtime_type,
        manifest_version: module.manifest_version,
        bridge_version: module.bridge_version,
        supports_auth_bridge: module.supports_auth_bridge,
        supports_telemetry: module.supports_telemetry,
        allowed_events: module.allowed_events,
        requires_login: module.requires_login,
        is_published: module.is_published,
        version: module.version
      }, authHeaders);
      setMessage({ type: 'success', text: `Module ${module.module_id} saved.` });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to save module.' });
    } finally {
      setSaving(false);
    }
  };

  const validateManifest = async () => {
    setSaving(true);
    setManifestStatus({ type: '', text: '', metadata: null });
    try {
      const manifest = parseManifestText(newModule.manifest_text);
      if (!manifest) {
        setManifestStatus({ type: 'info', text: 'No manifest pasted.', metadata: null });
        return null;
      }
      const response = await axios.post('/api/admin/modules/manifest/validate', manifest, authHeaders);
      setManifestStatus({
        type: 'success',
        text: `${response.data.runtime} manifest is valid.`,
        metadata: response.data
      });
      return manifest;
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Manifest validation failed.';
      setManifestStatus({ type: 'error', text: detail, metadata: null });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const createModule = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const manifest = parseManifestText(newModule.manifest_text);
      const response = await axios.post('/api/admin/modules', {
        module_id: newModule.module_id,
        title: newModule.title,
        subject: newModule.subject,
        subject_id: newModule.subject_id,
        build_path: newModule.build_path,
        cover_image_url: newModule.cover_image_url,
        runtime_type: newModule.runtime_type,
        module_manifest: manifest,
        description: ''
      }, authHeaders);
      setModules((prev) => [response.data, ...prev]);
      setNewModule({
        module_id: '',
        title: '',
        subject: 'physics',
        subject_id: null,
        build_path: '',
        cover_image_url: '',
        runtime_type: 'unity-webgl',
        manifest_text: ''
      });
      setManifestStatus({ type: '', text: '', metadata: null });
      setMessage({ type: 'success', text: 'Module created.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to create module.' });
    } finally {
      setSaving(false);
    }
  };

  const handleOrgChange = (id, field, value) => {
    setOrganizations((prev) => prev.map((org) => (
      org.id === id ? { ...org, [field]: value } : org
    )));
  };

  const saveOrg = async (org) => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await axios.put(`/api/admin/organizations/${org.id}`, {
        name: org.name,
        domain: org.domain,
        is_active: org.is_active,
        data_collection_enabled: org.data_collection_enabled
      }, authHeaders);
      setOrganizations((prev) => prev.map((item) => (item.id === org.id ? response.data : item)));
      setMessage({ type: 'success', text: `Organization ${org.name} saved.` });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to save organization.' });
    } finally {
      setSaving(false);
    }
  };

  const createOrg = async () => {
    const name = newOrg.name.trim();
    if (!name) return;
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await axios.post('/api/admin/organizations', {
        name,
        domain: newOrg.domain.trim() || null
      }, authHeaders);
      setOrganizations((prev) => [response.data, ...prev]);
      setNewOrg({ name: '', domain: '' });
      setMessage({ type: 'success', text: 'Organization created.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to create organization.' });
    } finally {
      setSaving(false);
    }
  };

  const sectionItems = [
    { key: 'overview', icon: <Shield size={16} />, label: 'Overview' },
    { key: 'users', icon: <Users size={16} />, label: 'Users' },
    { key: 'players', icon: <Activity size={16} />, label: 'Players' },
    ...(isPlatformAdmin ? [
      { key: 'telemetry', icon: <Database size={16} />, label: 'Data' },
      { key: 'templates', icon: <Mail size={16} />, label: 'Email Templates' },
      { key: 'modules', icon: <Gamepad2 size={16} />, label: 'Game Modules' },
      { key: 'orgs', icon: <Building2 size={16} />, label: 'Organizations' },
    ] : []),
  ];

  const quickActions = [
    {
      key: 'users',
      icon: <Users size={18} />,
      title: 'User Management',
      description: 'Search, deactivate, or delete users.'
    },
    {
      key: 'players',
      icon: <Activity size={18} />,
      title: 'Player Status',
      description: 'View module progress, active sessions, and xAPI exports.'
    },
    ...(isPlatformAdmin ? [
      {
        key: 'telemetry',
        icon: <Database size={18} />,
        title: 'Telemetry Exports',
        description: 'Download session files or export ZIPs.'
      },
      {
        key: 'modules',
        icon: <Gamepad2 size={18} />,
        title: 'Modules + Subjects',
        description: 'Publish modules and manage categories.'
      },
      {
        key: 'templates',
        icon: <Mail size={18} />,
        title: 'Email Templates',
        description: 'Welcome and password reset templates.'
      },
      {
        key: 'orgs',
        icon: <Building2 size={18} />,
        title: 'Organizations',
        description: 'Create and maintain organization records.'
      },
    ] : []),
  ];

  const subjectOptions = [
    { value: '', label: subjects.length ? 'Select subject' : 'No subjects configured' },
    ...subjects.map((subject) => ({ value: subject.id, label: subject.name })),
  ];

  const moduleStats = useMemo(() => {
    const published = modules.filter((module) => module.is_published).length;
    const bridgeEnabled = modules.filter((module) => module.supports_auth_bridge || module.supports_telemetry).length;
    const unityModules = modules.filter((module) => module.runtime_type === 'unity-webgl').length;
    const htmlModules = modules.filter((module) => module.runtime_type === 'html-iframe').length;

    return {
      total: modules.length,
      published,
      draft: Math.max(modules.length - published, 0),
      bridgeEnabled,
      unityModules,
      htmlModules,
    };
  }, [modules]);

  const selectedTemplate = useMemo(() => (
    templates.find((template) => template.key === selectedTemplateKey) || templates[0] || null
  ), [selectedTemplateKey, templates]);

  const templateColumns = [
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      fixed: 'left',
      render: (value) => <strong>{value}</strong>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'active',
      width: 120,
      render: (value) => <Tag color={value ? 'success' : 'default'}>{value ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (value) => <strong>{value}</strong>,
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 180,
      render: (value) => (value ? new Date(value).toLocaleString() : '-'),
    },
    {
      title: 'Body Length',
      dataIndex: 'body',
      key: 'body',
      width: 120,
      render: (value) => `${value?.length || 0} chars`,
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      width: 120,
      render: (_, template) => (
        <Button onClick={() => setSelectedTemplateKey(template.key)}>
          Edit
        </Button>
      ),
    },
  ];

  const emailLogColumns = [
    {
      title: 'Time',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 190,
      render: (value) => (value ? new Date(value).toLocaleString() : '-'),
    },
    {
      title: 'Recipient',
      dataIndex: 'recipient',
      key: 'recipient',
      render: (value) => <strong>{value}</strong>,
    },
    {
      title: 'Template',
      dataIndex: 'template_key',
      key: 'template_key',
      width: 170,
      render: (value) => (value ? <Tag>{value}</Tag> : <Tag>custom</Tag>),
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (value) => (
        <Tag color={value === 'sent' ? 'success' : 'error'}>{value || 'unknown'}</Tag>
      ),
    },
    {
      title: 'Error',
      dataIndex: 'error',
      key: 'error',
      ellipsis: true,
      render: (value) => value || '-',
    },
  ];

  const subjectColumns = [
    {
      title: 'Subject',
      dataIndex: 'name',
      key: 'name',
      render: (value) => <strong>{value}</strong>,
    },
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      render: (value) => <Tag>{value}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      render: (_, subject) => (
        <Button onClick={() => removeSubject(subject)} loading={saving}>
          Remove
        </Button>
      ),
    },
  ];

  const moduleColumns = [
    {
      title: 'Module',
      key: 'module',
      fixed: 'left',
      width: 230,
      render: (_, module) => (
        <div className="admin-table-identity module-title-cell">
          <Input
            size="small"
            value={module.title || ''}
            onChange={(event) => handleModuleChange(module.id, 'title', event.target.value)}
          />
          <Space className="module-tag-row" size={[6, 4]} wrap>
            <Tag>{module.module_id}</Tag>
            {module.version ? <Tag color="blue">v{module.version}</Tag> : null}
          </Space>
        </div>
      ),
    },
    {
      title: 'Bridge',
      key: 'bridge',
      width: 170,
      render: (_, module) => (
        <Space className="module-bridge-cell" size={[6, 6]} wrap>
          <Tag color="processing">{module.runtime_type || 'legacy'}</Tag>
          <Tag color={module.supports_auth_bridge || module.supports_telemetry ? 'success' : 'default'}>
            {describeBridge(module)}
          </Tag>
          {module.allowed_events?.length ? <Tag>{module.allowed_events.length} events</Tag> : null}
          {module.requires_login ? <Tag color="warning">Login required</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Subject',
      dataIndex: 'subject_id',
      key: 'subject',
      width: 150,
      render: (_, module) => (
        <Select
          size="small"
          className="module-subject-select"
          value={module.subject_id || ''}
          onChange={(value) => {
            const selectedId = Number(value) || null;
            const selected = subjects.find((subject) => subject.id === selectedId);
            handleModuleChange(module.id, 'subject_id', selectedId);
            handleModuleChange(module.id, 'subject', selected ? selected.key : '');
          }}
          disabled={!subjects.length}
          options={subjectOptions}
        />
      ),
    },
    {
      title: 'Build Prefix',
      dataIndex: 'build_path',
      key: 'build',
      width: 240,
      render: (_, module) => (
        <Tooltip title={module.build_path || ''}>
          <Input
            size="small"
            className="module-path-input"
            value={module.build_path || ''}
            placeholder="/games/example/Build/example"
            onChange={(event) => handleModuleChange(module.id, 'build_path', event.target.value)}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Cover Image URL',
      dataIndex: 'cover_image_url',
      key: 'cover',
      width: 220,
      render: (_, module) => (
        <Tooltip title={module.cover_image_url || ''}>
          <Input
            size="small"
            className="module-path-input"
            value={module.cover_image_url || ''}
            placeholder="/images/module-cover.png"
            onChange={(event) => handleModuleChange(module.id, 'cover_image_url', event.target.value)}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Published',
      dataIndex: 'is_published',
      key: 'published',
      width: 96,
      render: (_, module) => (
        <Switch
          className="module-publish-switch"
          checked={module.is_published}
          checkedChildren="Live"
          unCheckedChildren="Draft"
          onChange={(checked) => handleModuleChange(module.id, 'is_published', checked)}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      fixed: 'right',
      width: 96,
      render: (_, module) => (
        <Button className="module-action-cell" type="primary" icon={<Save size={16} />} loading={saving} onClick={() => saveModule(module)}>
          Save
        </Button>
      ),
    },
  ];

  const organizationColumns = [
    {
      title: 'Organization',
      key: 'organization',
      fixed: 'left',
      render: (_, org) => (
        <div className="admin-table-identity">
          <strong>{org.name} (#{org.id})</strong>
          <small>{org.domain || 'No domain'}</small>
        </div>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, org) => (
        <Input
          value={org.name || ''}
          onChange={(event) => handleOrgChange(org.id, 'name', event.target.value)}
          disabled={!isPlatformAdmin}
        />
      ),
    },
    {
      title: 'Domain',
      dataIndex: 'domain',
      key: 'domain',
      render: (_, org) => (
        <Input
          value={org.domain || ''}
          onChange={(event) => handleOrgChange(org.id, 'domain', event.target.value)}
          disabled={!isPlatformAdmin}
        />
      ),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'active',
      render: (_, org) => (
        <Switch
          checked={!!org.is_active}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          onChange={(checked) => handleOrgChange(org.id, 'is_active', checked)}
          disabled={!isPlatformAdmin}
        />
      ),
    },
    {
      title: 'Data Collection',
      dataIndex: 'data_collection_enabled',
      key: 'dataCollection',
      render: (_, org) => (
        <Switch
          checked={!!org.data_collection_enabled}
          checkedChildren="Data"
          unCheckedChildren="No data"
          onChange={(checked) => handleOrgChange(org.id, 'data_collection_enabled', checked)}
          disabled={!isPlatformAdmin}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      render: (_, org) => (
        <Button type="primary" icon={<Save size={16} />} loading={saving} onClick={() => saveOrg(org)}>
          Save
        </Button>
      ),
    },
  ];

  if (!isAdmin) {
    return (
      <InternalLayout
        title="Admin Center"
        description="Admin access required."
      >
        <Card className="admin-section-card">
          <p>You need an admin account to access this workspace.</p>
        </Card>
      </InternalLayout>
    );
  }

  return (
    <InternalLayout
      title="Admin Center"
      description="Manage users, modules, data exports, templates, and organizations."
      navItems={sectionItems}
      activeKey={activeSection}
      onNavChange={setActiveSection}
    >
      <div className="admin-center">

      {message.text && (
        <Alert
          className="account-message"
          type={message.type === 'success' ? 'success' : 'error'}
          showIcon
          message={message.text}
        />
      )}

        <div className="admin-center-content">
          {activeSection === 'overview' && (
            <Card className="admin-section-card" title={<Space><Shield size={18} />Quick Actions</Space>}>
              <div className="admin-center-cards">
                {quickActions.map((action) => (
                  <Card
                    hoverable
                    key={action.key}
                    className="admin-center-card"
                    onClick={() => setActiveSection(action.key)}
                  >
                    <div className="admin-center-card-body">
                      {action.icon}
                      <div>
                        <strong>{action.title}</strong>
                        <div className="admin-card-description">{action.description}</div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </Card>
          )}

          {activeSection === 'users' && <AdminUsers />}

          {activeSection === 'players' && <AdminPlayerData />}

          {activeSection === 'telemetry' && (
            isPlatformAdmin
              ? <AdminTelemetry />
              : <Card className="admin-section-card" title="Platform Admin Only"><p>You do not have access to data exports.</p></Card>
          )}

          {activeSection === 'templates' && (
            isPlatformAdmin ? (
            <Card className="admin-section-card admin-email-workbench" title={<Space><Mail size={18} />Email Templates</Space>}>
              <div className="admin-email-tabs">
                <Tabs
                  items={[
                  {
                    key: 'templates',
                    label: 'Templates',
                    children: (
                      <Row gutter={[16, 16]} className="admin-template-grid">
                        <Col xs={24} xl={14}>
                          <Card className="admin-template-list" title="Template Library">
                            <Table
                              className="admin-templates-table"
                              rowKey="id"
                              columns={templateColumns}
                              dataSource={templates}
                              loading={templatesLoading}
                              pagination={false}
                              scroll={{ x: 820 }}
                              rowClassName={(template) => (
                                template.key === selectedTemplate?.key ? 'is-selected-template' : ''
                              )}
                              onRow={(template) => ({
                                onClick: () => setSelectedTemplateKey(template.key)
                              })}
                              locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No email templates found." /> }}
                            />
                          </Card>
                        </Col>
                        <Col xs={24} xl={10}>
                          <Card
                            className="admin-template-editor"
                            title={selectedTemplate ? `Edit ${selectedTemplate.key}` : 'Edit Template'}
                            extra={selectedTemplate ? (
                              <Switch
                                checked={!!selectedTemplate.is_active}
                                checkedChildren="Active"
                                unCheckedChildren="Inactive"
                                onChange={(checked) => handleTemplateChange(selectedTemplate.id, 'is_active', checked)}
                              />
                            ) : null}
                          >
                            {selectedTemplate ? (
                              <Form layout="vertical">
                                <Form.Item label="Name">
                                  <Input
                                    value={selectedTemplate.name || ''}
                                    onChange={(event) => handleTemplateChange(selectedTemplate.id, 'name', event.target.value)}
                                  />
                                </Form.Item>
                                <Form.Item label="Subject">
                                  <Input
                                    value={selectedTemplate.subject || ''}
                                    onChange={(event) => handleTemplateChange(selectedTemplate.id, 'subject', event.target.value)}
                                  />
                                </Form.Item>
                                <Form.Item label="Body">
                                  <Input.TextArea
                                    rows={10}
                                    value={selectedTemplate.body || ''}
                                    onChange={(event) => handleTemplateChange(selectedTemplate.id, 'body', event.target.value)}
                                  />
                                </Form.Item>
                                <div className="admin-template-help">
                                  Available placeholders stay in double braces, for example <code>{'{{user_name}}'}</code> or <code>{'{{invite_code}}'}</code>.
                                </div>
                                <Button
                                  type="primary"
                                  icon={<Save size={16} />}
                                  loading={saving}
                                  onClick={() => saveTemplate(selectedTemplate)}
                                >
                                  Save Template
                                </Button>
                              </Form>
                            ) : (
                              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select a template to edit." />
                            )}
                          </Card>
                        </Col>
                      </Row>
                    ),
                  },
                  {
                    key: 'logs',
                    label: 'Delivery Logs',
                    children: (
                      <Card
                        className="admin-email-log-card"
                        title="Recent Email Activity"
                        extra={<Button onClick={() => fetchEmailLogs(1)} loading={emailLogsLoading}>Refresh</Button>}
                      >
                        <Table
                          className="admin-email-log-table"
                          rowKey="id"
                          columns={emailLogColumns}
                          dataSource={emailLogs}
                          loading={emailLogsLoading}
                          pagination={{
                            current: emailLogPage,
                            pageSize: 25,
                            total: emailLogTotal,
                            showSizeChanger: false,
                            onChange: (page) => fetchEmailLogs(page),
                          }}
                          scroll={{ x: 980 }}
                          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No email logs yet." /> }}
                        />
                      </Card>
                    ),
                  },
                  ]}
                />
              </div>
            </Card>
            ) : (
              <Card className="admin-section-card" title="Platform Admin Only"><p>You do not have access to email templates.</p></Card>
            )
          )}

          {activeSection === 'modules' && (
            isPlatformAdmin ? (
            <Card className="admin-section-card" title={<Space><Gamepad2 size={18} />Game Modules</Space>}>
              <Row className="admin-module-overview" gutter={[12, 12]}>
                <Col xs={24} sm={12} xl={6}>
                  <Card>
                    <Statistic title="Total Modules" value={moduleStats.total} />
                    <small>{moduleStats.published} published, {moduleStats.draft} drafts</small>
                  </Card>
                </Col>
                <Col xs={24} sm={12} xl={6}>
                  <Card>
                    <Statistic title="Bridge Ready" value={moduleStats.bridgeEnabled} />
                    <small>Auth or telemetry enabled</small>
                  </Card>
                </Col>
                <Col xs={24} sm={12} xl={6}>
                  <Card>
                    <Statistic title="Unity WebGL" value={moduleStats.unityModules} />
                    <small>Runtime type unity-webgl</small>
                  </Card>
                </Col>
                <Col xs={24} sm={12} xl={6}>
                  <Card>
                    <Statistic title="HTML iframe" value={moduleStats.htmlModules} />
                    <small>Runtime type html-iframe</small>
                  </Card>
                </Col>
              </Row>

              <Row className="admin-module-management-grid" gutter={[16, 16]}>
                <Col xs={24} xl={9}>
                  <Card className="subject-manager" title="Category Manager">
                    <p className="subject-helper">Add or remove subject tags that modules can use.</p>
                    <Form className="admin-subject-form" layout="inline" onFinish={createSubject}>
                      <Form.Item label="Subject">
                        <Input
                          value={newSubject.name}
                          onChange={(event) => setNewSubject({ name: event.target.value })}
                          placeholder="Add a subject"
                        />
                      </Form.Item>
                      <Form.Item>
                        <Button htmlType="submit" loading={saving}>Add</Button>
                      </Form.Item>
                    </Form>
                    <Table
                      className="admin-subjects-table"
                      rowKey="id"
                      columns={subjectColumns}
                      dataSource={visibleSubjects}
                      loading={subjectsLoading}
                      pagination={false}
                      scroll={{ x: 520 }}
                      locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No subjects configured yet." /> }}
                    />
                    <p className="subject-footnote">Use these names in module subject fields. Frontend filters use this list.</p>
                  </Card>
                </Col>
                <Col xs={24} xl={15}>
                  <Card className="admin-create-module-card" title="Create Module">
                    <Form className="admin-center-form" layout="vertical">
                      <Row gutter={[12, 0]}>
                        <Col xs={24} md={12}>
                          <Form.Item label="Module ID">
                            <Input
                              value={newModule.module_id}
                              onChange={(event) => setNewModule({ ...newModule, module_id: event.target.value })}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item label="Title">
                            <Input
                              value={newModule.title}
                              onChange={(event) => setNewModule({ ...newModule, title: event.target.value })}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item label="Subject">
                            <Select
                              value={newModule.subject_id || ''}
                              onChange={(value) => {
                                const selectedId = Number(value) || null;
                                const selected = subjects.find((subject) => subject.id === selectedId);
                                setNewModule({
                                  ...newModule,
                                  subject_id: selectedId,
                                  subject: selected ? selected.key : ''
                                });
                              }}
                              disabled={!subjects.length}
                              options={subjectOptions}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item label="Runtime">
                            <Select
                              value={newModule.runtime_type}
                              onChange={(value) => setNewModule({ ...newModule, runtime_type: value })}
                              options={runtimeOptions}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item label="Build Prefix">
                            <Input
                              value={newModule.build_path}
                              onChange={(event) => setNewModule({ ...newModule, build_path: event.target.value })}
                              placeholder="/games/Force&Motion/Build/20251122DrivingBuild"
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item label="Cover Image URL">
                            <Input
                              value={newModule.cover_image_url}
                              onChange={(event) => setNewModule({ ...newModule, cover_image_url: event.target.value })}
                              placeholder="/images/newton1-cover.png"
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24}>
                          <Form.Item label="Module Manifest JSON">
                            <Input.TextArea
                              rows={8}
                              className="module-manifest-input"
                              value={newModule.manifest_text}
                              onChange={(event) => {
                                setNewModule({ ...newModule, manifest_text: event.target.value });
                                setManifestStatus({ type: '', text: '', metadata: null });
                              }}
                              placeholder={'{"schema":"ping.game.module.v1","module_id":"newton1"}'}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Space className="module-manifest-actions" wrap>
                        <Button icon={<FileJson size={16} />} loading={saving} onClick={validateManifest}>
                          Validate Manifest
                        </Button>
                        {manifestStatus.text && (
                          <Tag className={`module-manifest-status is-${manifestStatus.type}`}>
                            {manifestStatus.type === 'success' && <CheckCircle2 size={15} />}
                            {manifestStatus.type === 'error' && <AlertTriangle size={15} />}
                            {manifestStatus.text}
                          </Tag>
                        )}
                      </Space>
                      {manifestStatus.metadata && (
                        <div className="module-bridge-status" aria-label="Validated manifest metadata">
                          <Tag>{manifestStatus.metadata.runtime}</Tag>
                          <Tag color={manifestStatus.metadata.supports_auth_bridge ? 'success' : 'default'}>
                            {manifestStatus.metadata.supports_auth_bridge ? 'Auth bridge' : 'No auth bridge'}
                          </Tag>
                          <Tag>{manifestStatus.metadata.allowed_events?.length || 0} events</Tag>
                        </div>
                      )}
                      <Button type="primary" icon={<Plus size={16} />} loading={saving} onClick={createModule}>
                        Create Module
                      </Button>
                    </Form>
                  </Card>
                </Col>
              </Row>

              <Card className="admin-modules-card" title="Published Game Catalog">
                <Table
                  className="admin-modules-table"
                  rowKey="id"
                  columns={moduleColumns}
                  dataSource={modules}
                  loading={modulesLoading}
                  pagination={{ pageSize: 8, showSizeChanger: false }}
                  scroll={{ x: 1550 }}
                  locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No modules found." /> }}
                />
              </Card>
            </Card>
            ) : (
              <Card className="admin-section-card" title="Platform Admin Only"><p>You do not have access to module management.</p></Card>
            )
          )}

          {activeSection === 'orgs' && (
            isPlatformAdmin ? (
            <Card className="admin-section-card" title={<Space><Building2 size={18} />Organizations</Space>}>

              {isPlatformAdmin && (
                <Card className="admin-create-org-card" title="Create Organization">
                  <Form className="admin-center-form" layout="vertical" onFinish={createOrg}>
                    <Form.Item label="Name">
                      <Input value={newOrg.name} onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })} />
                    </Form.Item>
                    <Form.Item label="Domain (optional)">
                      <Input value={newOrg.domain} onChange={(e) => setNewOrg({ ...newOrg, domain: e.target.value })} placeholder="school.edu" />
                    </Form.Item>
                    <Button type="primary" icon={<Plus size={16} />} loading={saving} onClick={createOrg}>
                      Create
                    </Button>
                  </Form>
                </Card>
              )}

              <Table
                className="admin-organizations-table"
                rowKey="id"
                columns={organizationColumns}
                dataSource={organizations}
                loading={orgLoading}
                pagination={false}
                scroll={{ x: 980 }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No organizations found." /> }}
              />
            </Card>
            ) : (
              <Card className="admin-section-card" title="Platform Admin Only"><p>You do not have access to organization management.</p></Card>
            )
          )}
        </div>
      </div>
    </InternalLayout>
  );
};

export default AdminCenter;
